/**
 * Lightweight wrapper around @react-google-maps/api.
 *
 * Single source of truth for the Google Maps JS API loader (so we don't
 * load the script twice — Google throws a warning + sometimes breaks
 * the map when that happens). Two components export:
 *
 *  • <GoogleMapView /> — generic map with markers + optional autopan
 *    follower. Used by OrderTrackingPage (customer-facing driver track)
 *    and AttendanceMapPage (HR clock-in pins).
 *
 *  • useGoogleMapsLoader — exposes `isLoaded` so callers can render
 *    spinners while the API bootstraps.
 *
 * The Google Maps API key is read from `REACT_APP_GOOGLE_MAPS_API_KEY`
 * and MUST be restricted to your domains in the Google Cloud Console
 * (HTTP referrer + APIs: Maps JavaScript API, Geocoding API).
 */
import React, { useEffect, useMemo, useRef } from 'react';
import { GoogleMap, Marker, InfoWindow, useJsApiLoader, Circle } from '@react-google-maps/api';

const DEFAULT_CENTER = { lat: 25.2048, lng: 55.2708 }; // Dubai

export function useGoogleMapsLoader() {
  // Stable libraries reference — passing a new array each render makes
  // @react-google-maps/api complain about "reloading the API".
  const libraries = useMemo(() => ['places'], []);
  return useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries,
  });
}

/**
 * `markers` shape:
 *   [{ id, position: {lat,lng}, label?, info?, iconUrl?, type? }]
 *
 * `follow` (optional): {lat,lng} — when it changes the map auto-pans to
 * this point. Used to keep the driver pin centered.
 */
export const GoogleMapView = ({
  height = '256px',
  center,
  zoom = 15,
  markers = [],
  follow = null,
  fitToMarkers = false,
  showAccuracyFor = null,   // { lat, lng, radius_m } — translucent circle
  className = '',
  testId = 'google-map',
}) => {
  const { isLoaded } = useGoogleMapsLoader();
  const mapRef = useRef(null);
  const [activeMarker, setActiveMarker] = React.useState(null);

  const resolvedCenter = center || markers[0]?.position || DEFAULT_CENTER;

  // Auto-follow a moving point (e.g. driver location)
  useEffect(() => {
    if (follow && mapRef.current && isLoaded) {
      mapRef.current.panTo(follow);
    }
  }, [follow?.lat, follow?.lng, isLoaded]);

  // Fit bounds to all markers when requested
  useEffect(() => {
    if (!fitToMarkers || !mapRef.current || !isLoaded || markers.length < 2) return;
    const bounds = new window.google.maps.LatLngBounds();
    markers.forEach((m) => m?.position && bounds.extend(m.position));
    mapRef.current.fitBounds(bounds, 60);
  }, [fitToMarkers, isLoaded, markers]);

  if (!process.env.REACT_APP_GOOGLE_MAPS_API_KEY) {
    return (
      <div
        className={`flex items-center justify-center text-xs text-wt-text-muted bg-wt-subtle/50 rounded ${className}`}
        style={{ height }}
        data-testid={`${testId}-missing-key`}
      >
        Google Maps API key not configured
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div
        className={`flex items-center justify-center text-xs text-wt-text-muted ${className}`}
        style={{ height }}
        data-testid={`${testId}-loading`}
      >
        Loading map…
      </div>
    );
  }

  return (
    <div className={className} style={{ width: '100%', height }} data-testid={testId}>
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%' }}
        center={resolvedCenter}
        zoom={zoom}
        onLoad={(m) => { mapRef.current = m; }}
        options={{
          disableDefaultUI: false,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          zoomControl: true,
          clickableIcons: false,
        }}
      >
        {markers.map((m) =>
          m?.position ? (
            <Marker
              key={m.id}
              position={m.position}
              label={m.label}
              icon={m.iconUrl ? { url: m.iconUrl, scaledSize: new window.google.maps.Size(36, 36) } : undefined}
              onClick={() => {
                if (!m.info) return;
                // Toggle behaviour: tapping the same marker again
                // closes the popup. On mobile the close X is small and
                // sometimes cut off the map edge, so a marker-tap close
                // is a much more discoverable affordance.
                setActiveMarker(prev => prev === m.id ? null : m.id);
              }}
            >
              {activeMarker === m.id && m.info && (
                <InfoWindow
                  onCloseClick={() => setActiveMarker(null)}
                  options={{
                    // Cap width so the bubble never overflows the
                    // map container on narrow screens (was filling
                    // the entire viewport on iPhones, hiding the
                    // close X). Google honours this as the max
                    // content width before wrap.
                    maxWidth: 200,
                    pixelOffset: new window.google.maps.Size(0, -8),
                    disableAutoPan: false,
                  }}
                >
                  <div className="text-[12px] leading-tight text-gray-900 px-0.5 py-0 max-w-[180px] whitespace-normal break-words">
                    {m.info}
                  </div>
                </InfoWindow>
              )}
            </Marker>
          ) : null
        )}

        {showAccuracyFor && (
          <Circle
            center={{ lat: showAccuracyFor.lat, lng: showAccuracyFor.lng }}
            radius={showAccuracyFor.radius_m || 25}
            options={{
              fillColor: '#3B82F6',
              fillOpacity: 0.12,
              strokeColor: '#3B82F6',
              strokeOpacity: 0.5,
              strokeWeight: 1,
            }}
          />
        )}
      </GoogleMap>
    </div>
  );
};

export default GoogleMapView;
